// The overlay INTERACTION — the DOM half of the field's pane overlay (lane
// L-OVL). The field (canon 75: the holographic terminal, route /field) projects
// an opened challenge OVER the feed, and in split lays two challenges as two
// live panes. This module makes those panes a first-class, polished physical
// interaction: a person can MOVE a pane (drag its grip), RESIZE it (drag an edge
// or corner), and the panes STACK — touching one brings it to the front. It is
// the missing "overlay drag/resize/stack" the island's own package.json
// describes, built to BEST (owner directive: no minimal).
//
// Framework-free (canon 91 §1): vanilla TS, no SPA framework, no runtime
// dependency; just the DOM and pointer events. All the geometry — the clamp, the
// resize floor, the hit-test, the z-order — lives in overlay-geometry.ts as PURE
// functions this module drives; this module owns only the DOM (reading pointer
// events, writing the pane's transform/size/z-index), mirroring how gestures.ts
// drives the pure recognizer.ts.
//
// Visual language (canon 83 "lights, not cells" / canon 94 "the charged
// identity"): a pane is NOT a window with a title bar and a chrome frame. The
// grip is a crisp charged seam at the pane's head (a short lit bar, the electric
// register), the resize affordance is the pane's own edge glowing where it can
// be grabbed — attachment-and-glow, not a fence. The ACTIVE pane (the one in
// front) carries the charge glow; the ones behind rest quiet. NO hand, cursor or
// figure is ever drawn (canon 83 §1.4): the only cursor hint is the native CSS
// `cursor` on a grabbable edge, which is the browser's pointer, not a thing the
// island paints onto the field. prefers-reduced-motion is honoured: the pane
// still moves/resizes (that is the interaction, not decoration), but the settle
// transition and the glow's breathing are dropped.
//
// The island owns its own runtime styling (an injected stylesheet + inline
// geometry), exactly the contract electric.ts and gestures.ts keep — it NEVER
// writes to web/css/field.css or tokens.css.
//
// POINTER RECONCILIATION vs the stroke recognizer (the deliberate rule, because
// both consume pointerdown on the same pane — see gestures.ts):
//   - A pointerdown on the pane's GRIP bar (`[data-overlay-grip]`) starts a MOVE.
//   - A pointerdown within the grab margin of a pane EDGE/CORNER starts a RESIZE.
//   - Anything else — the pane BODY (takes, keywords, the ring, empty space) — is
//     NOT intercepted: the event flows to the stroke recognizer and to HTMX
//     controls untouched, so drawing a connect/carry stroke across takes and
//     tapping links still work exactly as before.
// The grip and the resize edges are rendered as elements the recognizer already
// skips (gestures.ts leaves `button`/interactive controls alone, and we mark the
// grip a button and the edges data-overlay controls), so the two interactions
// never fight over the same press: the frame is the overlay's, the body is the
// grammar's.

import {
  bringToFront,
  clamp,
  DEFAULT_MIN,
  frontOf,
  handleAt,
  moveBy,
  resizeBy,
  zIndexOf,
  type Bounds,
  type Handle,
  type MinSize,
  type Rect,
} from "./overlay-geometry.js";
import { prefersReducedMotion, springTo, RAISE, SETTLE, type MotionHandle } from "./motion.js";

// The grab margin (px) around a pane's edge that reads as a resize handle. Wide
// enough for a finger on touch, narrow enough that the body stays the body.
const GRAB_MARGIN = 14;

// The island-owned overlay stylesheet: the grip seam, the active-pane charge
// glow, the edge cursors, and the reduced-motion opt-out. NOT a token and NOT
// field styling — it is the overlay mechanism's own runtime CSS, injected once
// into <head> (electric.ts / gestures.ts keep the identical contract for their
// keyframes). Colours consume the token layer's charge vars with literal
// fallbacks so they read before the page CSS loads and headless.
const STYLE_ID = "objexiv-island-overlay-style";
const STYLE = `
.objexiv-overlay {
  position: absolute;
  box-sizing: border-box;
  touch-action: none;
  will-change: transform, width, height;
}
@media (prefers-reduced-motion: no-preference) {
  .objexiv-overlay { transition: box-shadow 180ms ease; }
}
/* The ACTIVE (front) pane carries the charge. Attachment-and-glow, NOT a drawn
   border (canon 83 "lights, not cells"; canon 94 §1 "no card anywhere"): the
   front pane reads as a region of the ground LIT by the one loud charge —
   layered emission that bleeds outward (the --live bloom, like the live ring's
   rim) with only a whisper of a charged rim where it meets the ground. No hard
   outline rectangle (that read as a fenced card). The resting panes behind it
   carry none of this — they are quiet lifted ground. */
.objexiv-overlay[data-overlay-active="true"] {
  box-shadow:
    0 0 0 1px color-mix(in oklab, var(--live, oklch(0.82 0.17 60)) 14%, transparent),
    0 0 18px color-mix(in oklab, var(--live, oklch(0.82 0.17 60)) 20%, transparent),
    0 0 48px color-mix(in oklab, var(--live, oklch(0.82 0.17 60)) 16%, transparent),
    0 22px 60px color-mix(in oklab, black 55%, transparent);
}
@media (prefers-reduced-motion: no-preference) {
  .objexiv-overlay[data-overlay-active="true"] { animation: objexiv-overlay-charge 4.2s ease-in-out infinite; }
}
/* The charge breathes — the same cadence as a live ring's rim (field.css
   breathe, 4.2s), so the active pane reads as part of the one living surface,
   not a selected widget. The resting drop stays constant; only the --live bloom
   swells and settles. */
@keyframes objexiv-overlay-charge {
  0%, 100% { box-shadow:
    0 0 0 1px color-mix(in oklab, var(--live, oklch(0.82 0.17 60)) 12%, transparent),
    0 0 16px color-mix(in oklab, var(--live, oklch(0.82 0.17 60)) 16%, transparent),
    0 0 40px color-mix(in oklab, var(--live, oklch(0.82 0.17 60)) 12%, transparent),
    0 22px 60px color-mix(in oklab, black 55%, transparent); }
  50%      { box-shadow:
    0 0 0 1px color-mix(in oklab, var(--live, oklch(0.82 0.17 60)) 18%, transparent),
    0 0 24px color-mix(in oklab, var(--live, oklch(0.82 0.17 60)) 26%, transparent),
    0 0 56px color-mix(in oklab, var(--live, oklch(0.82 0.17 60)) 22%, transparent),
    0 22px 60px color-mix(in oklab, black 55%, transparent); }
}
/* The grip seam: a crisp lit bar at the pane's head the person grabs to move it.
   A short charged line (canon 94 electric register), not a titled chrome bar. It
   is a <button> so it is keyboard-reachable and the stroke recognizer leaves it
   alone (gestures.ts skips button). */
.objexiv-overlay-grip {
  display: block;
  width: 44px;
  height: 5px;
  margin: 6px auto 2px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: color-mix(in oklab, var(--live, oklch(0.82 0.17 60)) 55%, transparent);
  cursor: grab;
  touch-action: none;
}
.objexiv-overlay-grip:active { cursor: grabbing; }
.objexiv-overlay-grip:focus-visible {
  outline: 2px solid var(--live, oklch(0.82 0.17 60));
  outline-offset: 3px;
}
@media (prefers-reduced-motion: no-preference) {
  .objexiv-overlay[data-overlay-active="true"] .objexiv-overlay-grip {
    box-shadow: 0 0 10px color-mix(in oklab, var(--live-hot, oklch(0.95 0.11 75)) 55%, transparent);
  }
}
/* Native edge cursors — the browser's own pointer, the one cursor hint canon 83
   allows (it is the system cursor, not a figure the island draws on the field).
   Applied while a resize is possible under the pointer. */
.objexiv-overlay[data-overlay-edge="n"],
.objexiv-overlay[data-overlay-edge="s"] { cursor: ns-resize; }
.objexiv-overlay[data-overlay-edge="e"],
.objexiv-overlay[data-overlay-edge="w"] { cursor: ew-resize; }
.objexiv-overlay[data-overlay-edge="ne"],
.objexiv-overlay[data-overlay-edge="sw"] { cursor: nesw-resize; }
.objexiv-overlay[data-overlay-edge="nw"],
.objexiv-overlay[data-overlay-edge="se"] { cursor: nwse-resize; }
`;

function ensureStyle(doc: Document): void {
  if (!doc || !doc.head) return;
  if (doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement("style");
  style.id = STYLE_ID;
  style.textContent = STYLE;
  doc.head.appendChild(style);
}

// prefersReducedMotion is the island-wide reader in motion.ts (imported above) —
// one source of truth so the overlay's reduced-motion behaviour cannot drift
// from the electric register's or the ink's.

// ---- the panes the overlay governs -----------------------------------------

/** The field panes the overlay makes movable. In SPLIT (split.gohtml) the two
 *  `.pane` wrappers are the movable units — each holds a `.challenge-view`, which
 *  must NOT be governed on its own (that would position a pane inside a pane). In
 *  the single (non-split) states a bare `.challenge-view` IS the movable unit.
 *  So a pane is any element matching this selector that is NOT nested inside
 *  another matching element (the outermost wins) — see `isGovernable`. The
 *  render layer gives a challenge view the id `challenge-{uuid}`; a split pane we
 *  key by its side so the two are distinct in the stack. */
const PANE_SELECTOR = ".challenge-view, .split .pane";

/** True when an element is the OUTERMOST overlay pane — it matches the selector
 *  and no ANCESTOR of it (up to the field root) also matches. This is what keeps
 *  a split's nested `.challenge-view` from being governed separately from its
 *  `.pane` wrapper: the wrapper is governable, the nested view is not. */
function isGovernable(el: HTMLElement, root: HTMLElement): boolean {
  let p = el.parentElement;
  while (p && p !== root) {
    if (p.matches(PANE_SELECTOR)) return false;
    p = p.parentElement;
  }
  return true;
}

/** The stable overlay id for a pane element, used as its key in the z-order. A
 *  challenge view uses its render id; a split pane uses its side class so the
 *  two panes of one split are two stack entries. */
function overlayId(el: HTMLElement): string {
  if (el.id) return el.id;
  if (el.classList.contains("pane-a")) return "pane-a";
  if (el.classList.contains("pane-b")) return "pane-b";
  // Fallback: a stable per-element id minted once.
  if (!el.dataset.overlayId) el.dataset.overlayId = `overlay-${Math.random().toString(36).slice(2, 9)}`;
  return el.dataset.overlayId;
}

/** Read the pane's current rectangle in FIELD coordinates (its offset from the
 *  field's content box). Used as the drag/resize origin and to seed a pane that
 *  has not been positioned yet. */
function rectOf(pane: HTMLElement, fieldRect: DOMRect): Rect {
  const r = pane.getBoundingClientRect();
  return {
    x: r.left - fieldRect.left,
    y: r.top - fieldRect.top,
    width: r.width,
    height: r.height,
  };
}

/** The field's inner bounds a pane is clamped within. */
function boundsOf(field: HTMLElement): Bounds {
  return { width: field.clientWidth, height: field.clientHeight };
}

/** The pane's declared minimum size, if it set CSS min-width/min-height; else
 *  the default floor. Read from the computed style so a pane can ask to stay
 *  larger (a challenge ring wants room) without this module hard-coding it. */
function minOf(pane: HTMLElement): MinSize {
  const cs = typeof getComputedStyle === "function" ? getComputedStyle(pane) : null;
  const w = cs ? parseFloat(cs.minWidth) : NaN;
  const h = cs ? parseFloat(cs.minHeight) : NaN;
  return {
    width: Number.isFinite(w) && w > 0 ? w : DEFAULT_MIN.width,
    height: Number.isFinite(h) && h > 0 ? h : DEFAULT_MIN.height,
  };
}

/** Write a rectangle onto a pane as absolute field-space geometry. Position is a
 *  transform (compositor-cheap, no layout thrash during a drag); size is
 *  width/height. The pane is marked an overlay so the stylesheet governs it.
 *
 *  The transform carries a trailing `scale(var(--raise-scale,1))`: the raise POP
 *  springs that CSS variable (a brief lift above 1 that settles back), so the
 *  bring-to-front pop composites on top of the translate WITHOUT this geometry
 *  write and the scale spring fighting over one transform string. At rest the
 *  variable is 1 and the scale is a no-op. The probe parses the translate()
 *  head, which this keeps first and unchanged. */
function applyRect(pane: HTMLElement, rect: Rect): void {
  pane.classList.add("objexiv-overlay");
  pane.style.left = "0";
  pane.style.top = "0";
  pane.style.transform = `translate(${round2(rect.x)}px, ${round2(rect.y)}px) scale(var(--raise-scale, 1))`;
  pane.style.width = `${round2(rect.width)}px`;
  pane.style.height = `${round2(rect.height)}px`;
  pane.style.transformOrigin = "center";
}

function round2(f: number): number {
  return Math.round(f * 100) / 100;
}

// ---- the controller --------------------------------------------------------

/** A cleanup handle: detach the overlay interaction and restore the panes. */
export interface OverlayHandle {
  detach(): void;
  /** Re-scan the field for panes (after an HTMX swap replaced the fragment) and
   *  re-apply the stack. Idempotent. */
  refresh(): void;
}

/**
 * Mount the overlay interaction on the field root. It governs every pane under
 * the root (a challenge view, or the two split panes): makes each an absolutely-
 * positioned overlay, tracks their back-to-front stack, and drives move / resize
 * / bring-to-front from pointer events. Returns a handle to detach or re-scan.
 *
 * The listeners live on the ROOT (not per pane) and use pointer capture on the
 * root, so a drag that leaves the pane (fast pointer, past the edge) keeps
 * tracking — the same pointer-capture correctness the stroke recognizer relies
 * on. A pane is resolved per press by walking up from the event target, so a
 * fragment swap that replaces the panes does not stale the listeners.
 */
export function mountOverlays(root: HTMLElement): OverlayHandle {
  const doc = root.ownerDocument;
  ensureStyle(doc);

  // The field itself must be a positioning context so a pane's absolute geometry
  // is relative to the field, not the page. We set it only if the field is not
  // already positioned, and restore on detach.
  const prevPosition = root.style.position;
  if (getComputedStyle && getComputedStyle(root).position === "static") {
    root.style.position = "relative";
  }

  // Back-to-front stack of pane overlay-ids.
  let order: string[] = [];
  // Panes we have taken over, so detach can restore them.
  const governed = new Set<HTMLElement>();

  // The live drag/resize, or null between gestures.
  interface Active {
    pane: HTMLElement;
    handle: Handle;
    origin: Rect;
    startX: number;
    startY: number;
    pointerId: number;
    // The pane's live rectangle during the gesture — the spring settles FROM
    // where the finger left it, not from a recomputed box.
    last: Rect;
    // Velocity tracking: the last two pointer samples (position + timestamp) so
    // a RELEASE carries the finger's momentum into the settle spring. A flung
    // pane keeps travelling and gently overshoots; a slow release just settles.
    vx: number;
    vy: number;
    lastSampleX: number;
    lastSampleY: number;
    lastSampleT: number;
  }
  let active: Active | null = null;

  // Per-pane running settle/raise spring, so a new gesture on the same pane
  // cancels the previous settle rather than fighting it. Keyed by overlay id.
  const springs = new Map<string, MotionHandle>();

  /** Cancel any running spring on a pane (a fresh press takes over). */
  const stopSpring = (pane: HTMLElement) => {
    const id = overlayId(pane);
    springs.get(id)?.cancel();
    springs.delete(id);
  };

  /**
   * Spring a pane FROM its current rectangle TO a target rectangle, writing the
   * geometry every frame through applyRect so the canonical `translate(x,y)px` +
   * width/height form the drag uses (and the demo probe parses) stays the one
   * source of truth mid-spring. Momentum from the release seeds the x/y springs.
   *
   * Motion One drives a plain numeric carrier object (not the DOM node directly)
   * so all four values (x, y, width, height) spring together and we render them
   * ourselves — this keeps the probe contract exact and avoids Motion writing an
   * `x`/`y`/`width` transform form the rest of the code does not read. Under
   * reduced motion springTo sets the carrier instantly and applyRect lands the
   * final rectangle with no travel.
   */
  const springRect = (
    pane: HTMLElement,
    from: Rect,
    to: Rect,
    opts: { velocityX?: number; velocityY?: number; spring?: typeof SETTLE | typeof RAISE } = {},
  ) => {
    stopSpring(pane);
    pane.style.transition = "none"; // the spring owns motion now, not CSS
    if (prefersReducedMotion()) {
      applyRect(pane, to);
      return;
    }
    const carrier = { x: from.x, y: from.y, width: from.width, height: from.height };
    // The dominant release velocity seeds the shared spring (Motion takes one
    // velocity for the group; the larger axis carries the fling character).
    const velocity =
      Math.abs(opts.velocityX ?? 0) >= Math.abs(opts.velocityY ?? 0)
        ? opts.velocityX ?? 0
        : opts.velocityY ?? 0;
    const h = springTo(
      carrier as unknown as Element,
      { x: to.x, y: to.y, width: to.width, height: to.height },
      opts.spring ?? SETTLE,
      {
        velocity,
        onUpdate: () => applyRect(pane, { x: carrier.x, y: carrier.y, width: carrier.width, height: carrier.height }),
      },
    );
    springs.set(overlayId(pane), h);
    void h.finished.then(() => {
      // Land exactly on target (springs can stop a hair short) and restore the
      // resting transition so a later CSS cue (box-shadow) still eases.
      applyRect(pane, to);
      pane.style.transition = prefersReducedMotion() ? "none" : "";
      springs.delete(overlayId(pane));
    });
  };

  const paneFromTarget = (target: EventTarget | null): HTMLElement | null => {
    if (!(target instanceof Element)) return null;
    let pane: HTMLElement | null = target.closest<HTMLElement>(PANE_SELECTOR);
    if (!pane || !root.contains(pane)) return null;
    // Climb to the OUTERMOST pane: a press inside a split challenge body resolves
    // to the nested `.challenge-view`, but the movable unit is its `.pane`
    // wrapper. Walk up to the governable (outermost) pane.
    while (pane && !isGovernable(pane, root)) {
      const parent: HTMLElement | null = pane.parentElement?.closest<HTMLElement>(PANE_SELECTOR) ?? null;
      if (!parent || !root.contains(parent)) break;
      pane = parent;
    }
    return pane;
  };

  /** Give every pane under the root a z-index from the stack, and mark the front
   *  one active (it carries the charge glow). */
  const applyStack = () => {
    for (const pane of governed) {
      const id = overlayId(pane);
      pane.style.zIndex = String(zIndexOf(order, id));
      pane.dataset.overlayActive = String(frontOf(order) === id);
    }
  };

  /** Adopt a pane: position it as an overlay (seeded at its current laid-out
   *  place so nothing jumps), add it to the stack, and give it a grip. */
  const adopt = (pane: HTMLElement) => {
    if (governed.has(pane)) return;
    const fieldRect = root.getBoundingClientRect();
    const seed = clampSeed(rectOf(pane, fieldRect), boundsOf(root));
    applyRect(pane, seed);
    ensureGrip(pane);
    governed.add(pane);
    order = bringToFront(order, overlayId(pane));
  };

  /** A grip bar at the pane's head — the move affordance. A <button> so it is
   *  keyboard-reachable and the stroke recognizer skips it (gestures.ts leaves
   *  buttons alone). Inserted once, first child of the pane. */
  const ensureGrip = (pane: HTMLElement) => {
    if (pane.querySelector(":scope > .objexiv-overlay-grip")) return;
    const grip = doc.createElement("button");
    grip.type = "button";
    grip.className = "objexiv-overlay-grip";
    grip.dataset.overlayGrip = "true";
    grip.setAttribute("aria-label", "Move this challenge");
    // Keyboard move: arrows nudge the pane; the overlay is operable without a
    // pointer (canon 75 §4 access: every gesture has a keyboard equivalent).
    grip.addEventListener("keydown", (e) => onGripKey(e, pane));
    pane.insertBefore(grip, pane.firstChild);
  };

  /** Arrow-key nudging from a focused grip — the keyboard equivalent of a drag. */
  const onGripKey = (e: KeyboardEvent, pane: HTMLElement) => {
    const step = e.shiftKey ? 24 : 6;
    let dx = 0;
    let dy = 0;
    if (e.key === "ArrowLeft") dx = -step;
    else if (e.key === "ArrowRight") dx = step;
    else if (e.key === "ArrowUp") dy = -step;
    else if (e.key === "ArrowDown") dy = step;
    else return;
    e.preventDefault();
    raise(pane);
    const fieldRect = root.getBoundingClientRect();
    const next = moveBy(rectOf(pane, fieldRect), dx, dy, boundsOf(root));
    applyRect(pane, next);
  };

  /** Bring a pane to the front of the stack and re-apply z-indices, with a brief
   *  RAISE POP: the pane springs a small scale overshoot (1 → ~1.03 → 1) so the
   *  raise reads as "this one came forward" — a physical lift, not just a silent
   *  z-index swap. Only the pane that ACTUALLY moved to the front pops; re-raising
   *  the already-front pane is a no-op pop. Reduced motion: no pop, just the
   *  z-order. The scale composites via --raise-scale (see applyRect) so it never
   *  fights the translate geometry. */
  const raise = (pane: HTMLElement) => {
    const id = overlayId(pane);
    const wasFront = frontOf(order) === id;
    order = bringToFront(order, id);
    applyStack();
    if (wasFront || prefersReducedMotion()) return;
    // Pop: kick the scale up, then spring it back to 1. Motion animates the CSS
    // variable directly (WAAPI custom-property animation), so the compositor does
    // the scale and geometry is untouched.
    pane.style.setProperty("--raise-scale", "1.035");
    springTo(pane, { "--raise-scale": 1 } as Record<string, number>, RAISE).finished.then(() => {
      pane.style.setProperty("--raise-scale", "1");
    });
  };

  // ---- pointer gestures -----------------------------------------------------

  const onPointerDown = (e: PointerEvent) => {
    const pane = paneFromTarget(e.target);
    if (!pane) return;
    adopt(pane);

    // Touching any pane raises it — the stack's bring-to-front (even if this
    // press turns out to be a body press for the stroke recognizer, raising is
    // harmless and is what "interact with a pane brings it forward" means).
    raise(pane);

    const target = e.target as Element | null;
    const onGrip = !!target?.closest("[data-overlay-grip]");

    const fieldRect = root.getBoundingClientRect();
    const origin = rectOf(pane, fieldRect);
    const px = e.clientX - fieldRect.left;
    const py = e.clientY - fieldRect.top;

    let handle: Handle | null = null;
    if (onGrip) {
      handle = "move";
    } else {
      // A press within the grab margin of an edge/corner is a resize. Anywhere
      // else inside the pane is the BODY — we do NOT intercept it, so the stroke
      // recognizer and HTMX controls get the press untouched (the reconciliation
      // rule). handleAt returns null for the interior.
      handle = handleAt(origin, px, py, GRAB_MARGIN);
      // Only the OUTER ring of the body is a resize; an inner-edge false positive
      // on a tiny pane is avoided because handleAt already requires proximity to
      // the frame. If the press is on an interactive control, leave it (the
      // control, HTMX, or the recognizer owns it).
      if (handle && target?.closest("a, button, textarea, input, summary, [contenteditable]")) {
        handle = null;
      }
    }

    if (!handle) {
      // Body press: not ours. The recognizer (attached to the same root) will
      // handle it; we only raised the pane.
      return;
    }

    // Ours: begin a move or resize. A running settle spring on this pane is
    // cancelled — the finger takes over from wherever the pane currently is.
    stopSpring(pane);
    // Capture the pointer on the root so a fast drag past the pane keeps tracking
    // (pointer-capture correctness).
    active = {
      pane,
      handle,
      origin,
      startX: e.clientX,
      startY: e.clientY,
      pointerId: e.pointerId,
      last: origin,
      vx: 0,
      vy: 0,
      lastSampleX: e.clientX,
      lastSampleY: e.clientY,
      lastSampleT: e.timeStamp || performance.now(),
    };
    try {
      root.setPointerCapture(e.pointerId);
    } catch {
      // setPointerCapture can throw if the pointer is already gone; harmless.
    }
    // Stop the body interactions from also acting on this press — it is a frame
    // grab, decisively the overlay's.
    e.preventDefault();
    e.stopPropagation();
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!active) {
      // No gesture in flight: reflect the hoverable edge as a native cursor hint
      // so a grabbable edge shows the resize cursor (the one allowed cursor —
      // the system pointer, canon 83). Cheap; only updates a data attribute.
      hintEdge(e);
      return;
    }
    if (e.pointerId !== active.pointerId) return;
    const dx = e.clientX - active.startX;
    const dy = e.clientY - active.startY;
    const bounds = boundsOf(root);
    const next =
      active.handle === "move"
        ? moveBy(active.origin, dx, dy, bounds)
        : resizeBy(active.origin, active.handle, dx, dy, bounds, minOf(active.pane));
    // Track the pointer's instantaneous velocity (px/s) from the last sample, so
    // the RELEASE can hand the finger's momentum to the settle spring. A short
    // dt is floored so a burst of events doesn't divide by ~0.
    const now = e.timeStamp || performance.now();
    const dt = Math.max(8, now - active.lastSampleT); // ms, floored
    active.vx = ((e.clientX - active.lastSampleX) / dt) * 1000;
    active.vy = ((e.clientY - active.lastSampleY) / dt) * 1000;
    active.lastSampleX = e.clientX;
    active.lastSampleY = e.clientY;
    active.lastSampleT = now;
    active.last = next;
    // During an active drag, drop the settle transition so the pane tracks the
    // pointer 1:1 (the transition is for the bring-to-front settle, not the drag).
    active.pane.style.transition = "none";
    applyRect(active.pane, next);
    e.preventDefault();
  };

  const endGesture = (e: PointerEvent) => {
    if (!active || e.pointerId !== active.pointerId) return;
    const { pane, handle, last, vx, vy } = active;
    const bounds = boundsOf(root);
    try {
      root.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    active = null;

    // THE RELEASE — not a dead stop. The pane carries its release momentum one
    // step past where the finger left it, then the SETTLE spring pulls it back,
    // gently overshoots the clamped target and settles. A fling that would leave
    // the field is clamped, so the pane springs toward the field edge instead of
    // sailing off — momentum you can feel, geometry you can trust.
    //
    // For a MOVE the whole rectangle carries momentum (both axes). For a RESIZE
    // only the moved edge(s) spring, and with no fling momentum (a resize release
    // is a settle-into-place, not a throw) — the pane eases onto its final size.
    if (handle === "move") {
      // Project a short momentum step (capped) past `last`, then clamp: this is
      // the overshoot TARGET the spring eases back from, giving the settle its
      // life without letting a hard fling escape the field.
      const projX = clamp01Momentum(vx);
      const projY = clamp01Momentum(vy);
      const target = moveBy(last, projX, projY, bounds);
      springRect(pane, last, target, { velocityX: vx, velocityY: vy, spring: SETTLE });
    } else {
      // Resize settle: the moved edge carries a SMALL momentum past `last` and
      // springs back — the pane's size gently overshoots and settles rather than
      // stopping the instant the finger lifts. The projection is damped harder
      // than a move (a resize fling should be subtle, not a bounce) and clamped
      // to the field + floor by resizeBy, so the overshoot is always valid.
      const projX = clamp01Momentum(vx) * 0.5;
      const projY = clamp01Momentum(vy) * 0.5;
      const target = resizeBy(last, handle, projX, projY, bounds, minOf(pane));
      springRect(pane, last, target, { velocityX: vx, velocityY: vy, spring: SETTLE });
    }
  };

  /** Project a release velocity (px/s) into a short momentum step (px), capped so
   *  the overshoot is lively but bounded — a hard fling overshoots a fixed, small
   *  distance rather than a huge one the spring would take an age to reel back. */
  function clamp01Momentum(vPxPerS: number): number {
    const step = vPxPerS * 0.08; // ~80ms of travel at release speed
    const CAP = 90; // px — the most a fling overshoots before the spring reels it
    return clamp(step, -CAP, CAP);
  }

  /** Reflect which edge the pointer is over as a data attribute, so the
   *  stylesheet shows the matching native resize cursor. No effect during a
   *  drag; purely a hover hint. */
  const hintEdge = (e: PointerEvent) => {
    const pane = paneFromTarget(e.target);
    if (!pane || !governed.has(pane)) return;
    const fieldRect = root.getBoundingClientRect();
    const rect = rectOf(pane, fieldRect);
    const px = e.clientX - fieldRect.left;
    const py = e.clientY - fieldRect.top;
    const target = e.target as Element | null;
    const onControl = !!target?.closest("a, button, textarea, input, summary, [contenteditable], [data-overlay-grip]");
    const h = onControl ? null : handleAt(rect, px, py, GRAB_MARGIN);
    if (h && h !== "move") pane.dataset.overlayEdge = h;
    else delete pane.dataset.overlayEdge;
  };

  root.addEventListener("pointerdown", onPointerDown);
  root.addEventListener("pointermove", onPointerMove);
  root.addEventListener("pointerup", endGesture);
  root.addEventListener("pointercancel", endGesture);

  const refresh = () => {
    // Adopt any newly-swapped-in panes and drop any that left the DOM. Only the
    // OUTERMOST matching elements are governed (a split's nested .challenge-view
    // rides inside its .pane wrapper, never positioned on its own).
    for (const pane of Array.from(root.querySelectorAll<HTMLElement>(PANE_SELECTOR))) {
      if (isGovernable(pane, root)) adopt(pane);
    }
    for (const pane of Array.from(governed)) {
      if (!root.contains(pane)) {
        governed.delete(pane);
        order = order.filter((o) => o !== overlayId(pane));
      }
    }
    applyStack();
  };

  // RESPONSIVE REFLOW (canon 83 §2 — the device/rotation matrix): when the field
  // itself resizes (viewport resize, phone rotation portrait↔landscape, an HTMX
  // swap changing the layout), every pane is re-clamped into the new bounds and
  // springs to its corrected rectangle, so nothing is left stranded off the
  // shrunken field or bunched after a rotation. A ResizeObserver on the field is
  // the right primitive (it fires on the element's own box, not just window
  // resize, so it also catches a container the HTMX fragment reflowed). The
  // reflow SPRINGS each pane to its clamped spot — a graceful re-settle on
  // rotation, not a hard jump. Debounced to a frame so a drag-resize of the
  // window is one settle, not a storm.
  let reflowRaf = 0;
  const reflow = () => {
    const bounds = boundsOf(root);
    const fieldRect = root.getBoundingClientRect();
    for (const pane of governed) {
      // Skip the pane under an active gesture — the finger owns it.
      if (active && active.pane === pane) continue;
      const cur = rectOf(pane, fieldRect);
      const clamped = clampSeed(cur, bounds);
      // Only re-settle if the clamp actually moved/shrank the pane (it left the
      // new bounds); an unchanged pane is left exactly where it is.
      if (Math.abs(clamped.x - cur.x) > 0.5 || Math.abs(clamped.y - cur.y) > 0.5 || Math.abs(clamped.width - cur.width) > 0.5 || Math.abs(clamped.height - cur.height) > 0.5) {
        springRect(pane, cur, clamped, { spring: SETTLE });
      }
    }
  };
  const onFieldResize = () => {
    if (reflowRaf) cancelAnimationFrame(reflowRaf);
    reflowRaf = requestAnimationFrame(reflow);
  };
  let resizeObserver: ResizeObserver | null = null;
  if (typeof ResizeObserver !== "undefined") {
    resizeObserver = new ResizeObserver(onFieldResize);
    resizeObserver.observe(root);
  } else if (typeof window !== "undefined") {
    // Fallback for a headless/old environment with no ResizeObserver.
    window.addEventListener("resize", onFieldResize);
  }

  // Initial scan.
  refresh();

  return {
    detach() {
      if (reflowRaf) cancelAnimationFrame(reflowRaf);
      resizeObserver?.disconnect();
      if (typeof window !== "undefined") window.removeEventListener("resize", onFieldResize);
      for (const h of springs.values()) h.cancel();
      springs.clear();
      root.removeEventListener("pointerdown", onPointerDown);
      root.removeEventListener("pointermove", onPointerMove);
      root.removeEventListener("pointerup", endGesture);
      root.removeEventListener("pointercancel", endGesture);
      for (const pane of governed) {
        pane.classList.remove("objexiv-overlay");
        pane.style.transform = "";
        pane.style.width = "";
        pane.style.height = "";
        pane.style.left = "";
        pane.style.top = "";
        pane.style.zIndex = "";
        pane.style.transition = "";
        pane.style.transformOrigin = "";
        pane.style.removeProperty("--raise-scale");
        delete pane.dataset.overlayActive;
        delete pane.dataset.overlayEdge;
        pane.querySelector(":scope > .objexiv-overlay-grip")?.remove();
      }
      governed.clear();
      order = [];
      active = null;
      root.style.position = prevPosition;
    },
    refresh,
  };
}

/** Seed a freshly-adopted pane's rectangle: its current laid-out box, clamped
 *  into the field. A pane that currently has no size (display quirk) is given
 *  the default floor so it is grabbable. */
function clampSeed(rect: Rect, bounds: Bounds): Rect {
  const width = rect.width > 0 ? rect.width : DEFAULT_MIN.width;
  const height = rect.height > 0 ? rect.height : DEFAULT_MIN.height;
  const seeded: Rect = { x: rect.x, y: rect.y, width, height };
  return {
    x: clamp(seeded.x, 0, Math.max(0, bounds.width - width)),
    y: clamp(seeded.y, 0, Math.max(0, bounds.height - height)),
    width,
    height,
  };
}
